import { useCallback, useEffect, useState } from 'react';
import {
  Clock3,
  Film,
  Play,
  RefreshCw,
  Square,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import {
  cleanStaleFlowkitVideos,
  deleteUgcFlowkitBatch,
  listUgcFlowkitBatches,
  resumeUgcFlowkitBatch,
  stopUgcFlowkitBatch,
  type UgcFlowkitBatchSummary,
} from '../services/ugcFlowkit';

function formatTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export interface UgcBatchesQueueViewProps {
  selectedBatchId?: string | null;
  onSelectBatch?: (batchId: string) => void;
  onActionComplete?: (action: 'stop' | 'resume' | 'delete' | 'clean', batchId?: string) => void;
  title?: string;
  subtitle?: string;
}

export default function UgcBatchesQueueView({
  selectedBatchId,
  onSelectBatch,
  onActionComplete,
  title = 'Lịch sử Batches & Hàng đợi (Queue)',
  subtitle = 'Chỉ 1 batch chạy tại một thời điểm để bảo đảm an toàn chống WAF/reCAPTCHA. Các batch sau sẽ tự động xếp hàng và chạy tuần tự.',
}: UgcBatchesQueueViewProps) {
  const [batchesList, setBatchesList] = useState<UgcFlowkitBatchSummary[]>([]);
  const [queueInfo, setQueueInfo] = useState<{ activeBatchId: string | null; queuedBatchIds: string[] }>({
    activeBatchId: null,
    queuedBatchIds: [],
  });
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [cleaningStale, setCleaningStale] = useState(false);
  const [staleCleanResult, setStaleCleanResult] = useState<string | null>(null);
  const [batchActionBusy, setBatchActionBusy] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  const loadBatches = useCallback(async () => {
    setLoadingBatches(true);
    try {
      const res = await listUgcFlowkitBatches();
      setBatchesList(res.batches);
      setQueueInfo(res.queue);
      setError('');
    } catch (err: unknown) {
      console.error('Failed to load batches:', err);
      setError(err instanceof Error ? err.message : 'Không thể tải danh sách batches');
    } finally {
      setLoadingBatches(false);
    }
  }, []);

  useEffect(() => {
    void loadBatches();
    const timer = setInterval(() => {
      void loadBatches();
    }, 10000);
    return () => clearInterval(timer);
  }, [loadBatches]);

  const handleCleanStale = async () => {
    if (!window.confirm('Dọn dẹp toàn bộ video nháp tồn đọng trên Flowkit? Thao tác này sẽ loại bỏ các job ma/draft không thuộc batch đang hoạt động.')) return;
    setCleaningStale(true);
    setStaleCleanResult(null);
    try {
      const res = await cleanStaleFlowkitVideos();
      setStaleCleanResult(`Đã dọn dẹp thành công ${res.deletedVideoIds.length} video nháp tồn đọng trên Flowkit.`);
      await loadBatches();
      onActionComplete?.('clean');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Không thể dọn dẹp Flowkit draft');
    } finally {
      setCleaningStale(false);
    }
  };

  const handleStopBatch = async (batchId: string) => {
    setBatchActionBusy(prev => ({ ...prev, [batchId]: 'stopping' }));
    try {
      await stopUgcFlowkitBatch(batchId);
      await loadBatches();
      onActionComplete?.('stop', batchId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Không thể dừng batch');
    } finally {
      setBatchActionBusy(prev => {
        const next = { ...prev };
        delete next[batchId];
        return next;
      });
    }
  };

  const handleResumeBatch = async (batchId: string) => {
    setBatchActionBusy(prev => ({ ...prev, [batchId]: 'resuming' }));
    try {
      await resumeUgcFlowkitBatch(batchId);
      await loadBatches();
      onActionComplete?.('resume', batchId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Không thể tiếp tục batch');
    } finally {
      setBatchActionBusy(prev => {
        const next = { ...prev };
        delete next[batchId];
        return next;
      });
    }
  };

  const handleDeleteBatch = async (batchId: string) => {
    if (!window.confirm(`Xóa vĩnh viễn batch ${batchId} và lịch sử chạy liên quan?`)) return;
    setBatchActionBusy(prev => ({ ...prev, [batchId]: 'deleting' }));
    try {
      await deleteUgcFlowkitBatch(batchId);
      await loadBatches();
      onActionComplete?.('delete', batchId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Không thể xóa batch');
    } finally {
      setBatchActionBusy(prev => {
        const next = { ...prev };
        delete next[batchId];
        return next;
      });
    }
  };

  return (
    <section className="space-y-4">
      {/* Top Banner & Action Buttons */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#d6bd7c] bg-[#fff8ec] p-4 shadow-sm">
        <div>
          <h3 className="text-sm font-black text-[#2f2414]">{title}</h3>
          <p className="mt-1 text-xs text-[#6d5b3f]">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void loadBatches()}
            disabled={loadingBatches}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#d6bd7c] bg-white px-3 py-1.5 text-xs font-bold text-[#6d5b3f] hover:bg-[#e9d6aa]/50 disabled:opacity-60 transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loadingBatches ? 'animate-spin' : ''}`} />
            Làm mới
          </button>
          <button
            type="button"
            onClick={() => void handleCleanStale()}
            disabled={cleaningStale}
            title="Dọn các video queued/draft ma đang bị kẹt trên Flowkit"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#dc553c]/40 bg-[#dc553c]/10 px-3 py-1.5 text-xs font-bold text-[#8c2d1f] hover:bg-[#dc553c]/20 disabled:opacity-60 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {cleaningStale ? 'Đang dọn dẹp…' : 'Dọn dẹp Draft/Queue Flowkit'}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-[#dc553c]/40 bg-[#dc553c]/10 p-3 text-xs font-bold text-[#8c2d1f]">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {staleCleanResult && (
        <div className="flex items-center gap-2 rounded-xl border border-[#b87a14]/40 bg-[#b87a14]/15 p-3 text-xs font-bold text-[#6f4810]">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>{staleCleanResult}</span>
        </div>
      )}

      {/* Queue Status Box */}
      <div className="rounded-xl border border-[#b54426]/35 bg-[#b54426]/10 p-3 text-xs text-[#8f3219] space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-bold">Batch đang chạy:</span>
          {queueInfo.activeBatchId ? (
            <code className="font-mono font-bold bg-white/70 px-2 py-0.5 rounded border border-[#b54426]/30 break-all">
              {queueInfo.activeBatchId}
            </code>
          ) : (
            <span className="text-[#6d5b3f] italic">Không có batch nào đang chạy</span>
          )}
        </div>
        {queueInfo.queuedBatchIds.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <span className="font-bold">Hàng đợi ({queueInfo.queuedBatchIds.length}):</span>
            {queueInfo.queuedBatchIds.map((id, idx) => (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded bg-white/80 px-2 py-0.5 font-mono text-[11px] font-bold text-[#2f2414] border border-[#d6bd7c]/60"
              >
                <Clock3 className="h-3 w-3 text-[#865916]" />
                #{idx + 1} {id.slice(0, 16)}…
              </span>
            ))}
          </div>
        ) : (
          <div className="text-[11px] text-[#6d5b3f]">Hàng đợi trống. Batch mới submit sẽ được chạy ngay lập tức.</div>
        )}
      </div>

      {/* Batches List */}
      <div className="divide-y divide-[#d6bd7c]/40 rounded-2xl border border-[#d6bd7c] bg-[#fff8ec] shadow-sm overflow-hidden">
        {batchesList.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6d5b3f]">Chưa có batch nào được tạo.</div>
        ) : (
          batchesList.map(b => {
            const isActive = b.id === selectedBatchId;
            const isQueueActive = b.id === queueInfo.activeBatchId;
            const qPos = queueInfo.queuedBatchIds.indexOf(b.id);
            const isEnqueued = qPos >= 0;
            const busy = batchActionBusy[b.id];

            return (
              <div
                key={b.id}
                className={`p-4 transition-colors ${
                  isActive ? 'bg-[#b54426]/10 border-l-4 border-l-[#b54426]' : 'hover:bg-white/40'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs font-black text-[#2f2414] break-all">{b.id}</span>
                      {isActive && (
                        <span className="rounded bg-[#b54426] px-2 py-0.5 text-[10px] font-black text-white">
                          Đang xem
                        </span>
                      )}
                      {isQueueActive && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-[#b54426]/40 bg-[#b54426]/15 px-2 py-0.5 text-[10px] font-black text-[#8f3219]">
                          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#b54426]" /> Đang chạy
                        </span>
                      )}
                      {isEnqueued && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-[#d6bd7c] bg-[#fdf6e9] px-2 py-0.5 text-[10px] font-black text-[#6d5b3f]">
                          <Clock3 className="h-3 w-3 text-[#865916]" /> Hàng đợi #{qPos + 1}
                        </span>
                      )}
                      {!isQueueActive && !isEnqueued && b.status === 'completed' && (
                        <span className="rounded-full border border-[#b87a14]/45 bg-[#b87a14]/15 px-2 py-0.5 text-[10px] font-black text-[#6f4810]">
                          Hoàn tất
                        </span>
                      )}
                      {!isQueueActive && !isEnqueued && b.status === 'stopped' && (
                        <span className="rounded-full border border-[#dc553c]/40 bg-[#dc553c]/15 px-2 py-0.5 text-[10px] font-black text-[#8c2d1f]">
                          Đã dừng
                        </span>
                      )}
                      {b.status === 'blocked' && (
                        <span className="rounded-full border border-[#dc553c]/40 bg-[#dc553c]/15 px-2 py-0.5 text-[10px] font-black text-[#8c2d1f]">
                          Tạm dừng ({b.blockedCode || 'Bridge'})
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#6d5b3f]">
                      <span>Tạo lúc: {formatTime(b.createdAt)}</span>
                      <span>
                        Chế độ: <strong>{b.config.mode === 'character' ? 'Family Character' : 'Faceless'}</strong>
                      </span>
                      <span>
                        Sản phẩm: <strong>{b.totalProducts}</strong> (
                        <span className="text-[#6f4810] font-bold">{b.completedProducts} xong</span>,{' '}
                        <span className="text-[#8f3219] font-bold">{b.runningProducts} đang chạy</span>,{' '}
                        <span className="text-[#8c2d1f] font-bold">{b.stoppedProducts} dừng</span>)
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {onSelectBatch && (
                      <button
                        type="button"
                        onClick={() => onSelectBatch(b.id)}
                        className="rounded-lg border border-[#b54426]/50 bg-white px-2.5 py-1 text-xs font-bold text-[#8f3219] hover:bg-[#b54426]/15 transition-colors"
                      >
                        {isActive ? 'Đang hiển thị' : 'Xem kịch bản & Prompts'}
                      </button>
                    )}
                    {(isQueueActive || isEnqueued || b.status === 'running') && (
                      <button
                        type="button"
                        onClick={() => void handleStopBatch(b.id)}
                        disabled={Boolean(busy)}
                        className="inline-flex items-center gap-1 rounded-lg border border-[#dc553c]/50 bg-white px-2.5 py-1 text-xs font-bold text-[#8c2d1f] hover:bg-[#dc553c]/15 disabled:opacity-50 transition-colors"
                      >
                        <Square className="h-2.5 w-2.5 fill-current" />
                        {busy === 'stopping' ? 'Đang dừng…' : 'Dừng'}
                      </button>
                    )}
                    {(b.status === 'stopped' || b.status === 'blocked') && (
                      <button
                        type="button"
                        onClick={() => void handleResumeBatch(b.id)}
                        disabled={Boolean(busy)}
                        className="inline-flex items-center gap-1 rounded-lg border border-[#b54426]/50 bg-white px-2.5 py-1 text-xs font-bold text-[#8f3219] hover:bg-[#b54426]/15 disabled:opacity-50 transition-colors"
                      >
                        <Play className="h-2.5 w-2.5 fill-current" />
                        {busy === 'resuming' ? 'Đang tiếp tục…' : 'Tiếp tục'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void handleDeleteBatch(b.id)}
                      disabled={Boolean(busy)}
                      className="inline-flex items-center gap-1 rounded-lg border border-[#dc553c]/35 bg-white px-2.5 py-1 text-xs font-bold text-[#8c2d1f] hover:bg-[#dc553c]/15 disabled:opacity-50 transition-colors"
                    >
                      <Trash2 className="h-3 w-3" />
                      {busy === 'deleting' ? 'Đang xóa…' : 'Xóa'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
